import { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { formatLocalDateOnly } from '../../shared utils/dateFormatters.js';
import { SERVER_URL } from '../../../constants.js';

function AddStudent(props) {
  const [open, setOpen] = useState(false);
  const [templateStudentId, setTemplateStudentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  const showToast = (msg, severity = 'success') => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const [student, setStudent] = useState({
    dateAdded: new Date(),
    mtRef: '',
    studentName: '',
    studentSurname: '',
    studentDob: null,
    studentGender: '',
    studentNationality: '',
    englishLevel: '',
    tshirtSize: '',
    roomRequirements: '',
    classRequirements: '',
    allergies: '',
    notes: '',
    emergencyContact: '',
    mobilePhoneOptOut: false,
    hasAllPermission: false,
    hasPoolPermission: false,
    hasPhotoPermission: false,
    hasMedicalPermission: false,
    hasHospitalPermission: false,
    hasExcursionPermission: false,
    hasActivityPermission: false,
    hasSupervisionPermission: false,
  });

  const handleClickOpen = () => {
    const today = new Date();
    setStudent(prevStudent => ({
      ...prevStudent,
      dateAdded: today,
    }));
    setTemplateStudentId('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date, name) => {
    setStudent(prevStudent => ({
      ...prevStudent,
      [name]: date,
    }));
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    if (name === 'hasAllPermission') {
      const newPermissions = {
        hasAllPermission: checked,
        hasPoolPermission: checked,
        hasPhotoPermission: checked,
        hasMedicalPermission: checked,
        hasHospitalPermission: checked,
        hasExcursionPermission: checked,
        hasActivityPermission: checked,
        hasSupervisionPermission: checked,
      };

      setStudent(prevStudent => ({
        ...prevStudent,
        ...newPermissions,
      }));
    } else if (type === 'checkbox') {
      setStudent(prevStudent => ({
        ...prevStudent,
        [name]: checked,
      }));
    } else {
      setStudent(prevStudent => ({
        ...prevStudent,
        [name]: value,
      }));
    }
  };

  const duplicateFromTemplate = async (newStudentId, templateId) => {
    const token = sessionStorage.getItem('bearer');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchJson = async (url) => {
      const res = await fetch(url, { headers });
      if (res.status === 204) return [];
      if (!res.ok) throw new Error(`Failed to fetch ${url}`);
      return res.json();
    };

    const [stays, transfers, bookings] = await Promise.all([
      fetchJson(`${SERVER_URL}api/student/${templateId}/stays`),
      fetchJson(`${SERVER_URL}api/transfers/${templateId}`),
      fetchJson(`${SERVER_URL}api/student/${templateId}/booking`),
    ]);

    let copied = { stays: 0, transfers: 0, bookings: 0 };

    for (const stay of stays) {
      await fetch(`${SERVER_URL}api/student/${newStudentId}/stay`, {
        method: 'POST', headers,
        body: JSON.stringify({
          campus: stay.campus,
          arrivalDate: stay.arrivalDate,
          departureDate: stay.departureDate,
        }),
      });
      copied.stays++;
    }

    for (const t of transfers) {
      await fetch(`${SERVER_URL}api/transfers/${newStudentId}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          direction: t.direction,
          transferDate: t.transferDate,
          depart: t.depart,
          arrive: t.arrive,
          privatePickup: t.privatePickup,
          departureTime: t.departureTime,
          arrivalTime: t.arrivalTime,
          flightId: t.flightId,
          unaccompaniedMinor: t.unaccompaniedMinor,
          notes: t.notes,
          internalTransfer: t.internalTransfer,
        }),
      });
      copied.transfers++;
    }

    for (const b of bookings) {
      await fetch(`${SERVER_URL}api/bookings/studentId/${newStudentId}/productId/${b.productId}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          actualCharge: b.actualCharge,
          bookingStatus: b.bookingStatus,
        }),
      });
      copied.bookings++;
    }

    return copied;
  };

  const handleSave = async () => {
    const hasDetails = student.studentName.trim() || student.studentSurname.trim();
    if (!hasDetails) {
      const confirmed = window.confirm(
        "You didn't add any student details, are you sure you want to create an empty student record?"
      );
      if (!confirmed) return;
    }

    const formattedStudent = {
      ...student,
      dateAdded: formatLocalDateOnly(student.dateAdded),
      studentDob: formatLocalDateOnly(student.studentDob),
    };

    setSaving(true);
    try {
      const newStudentId = await props.addStudent(formattedStudent);
      if (!newStudentId) {
        setSaving(false);
        return;
      }

      if (templateStudentId.trim()) {
        try {
          const copied = await duplicateFromTemplate(newStudentId, templateStudentId.trim());
          showToast(
            `Student created. Copied ${copied.stays} stay(s), ${copied.transfers} flight detail(s), ${copied.bookings} booked product(s).`,
            'success'
          );
        } catch (err) {
          console.error('Error duplicating from template:', err);
          showToast('Student created but some data failed to copy.', 'warning');
        }
      }

      handleClose();
    } catch (err) {
      console.error('Error creating student:', err);
      showToast('Failed to create student.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <IconButton onClick={handleClickOpen}>
        <AddIcon color="primary" />
      </IconButton>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Student</DialogTitle>
        <DialogContent sx={{ minWidth: 600, minHeight: 600 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              sx={{ mt: 2 }}
            >
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  label="Date Added"
                  value={student.dateAdded}
                  onChange={(date) => handleDateChange(date, 'dateAdded')}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth sx={{ mb: 0, mt: 0 }} />
                  )}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Master Tracker ID"
                  value={student.mtRef || ''}
                  onChange={handleChange}
                  fullWidth
                  name="mtRef"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Name"
                  value={student.studentName || ''}
                  onChange={handleChange}
                  fullWidth
                  name="studentName"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Surname"
                  value={student.studentSurname || ''}
                  onChange={handleChange}
                  fullWidth
                  name="studentSurname"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  label="Student Date Of Birth"
                  value={student.studentDob}
                  onChange={(date) => handleDateChange(date, 'studentDob')}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth sx={{ mb: 0, mt: 0 }} />
                  )}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Gender"
                  value={student.studentGender || ''}
                  onChange={handleChange}
                  fullWidth
                  name="studentGender"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Nationality"
                  value={student.studentNationality || ''}
                  onChange={handleChange}
                  fullWidth
                  name="studentNationality"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="English Level"
                  value={student.englishLevel || ''}
                  onChange={handleChange}
                  fullWidth
                  name="englishLevel"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <InputLabel id="tshirt-label">T-Shirt Size</InputLabel>
                  <Select
                    labelId="tshirt-label"
                    label="T-Shirt Size"
                    name="tshirtSize"
                    value={student.tshirtSize}
                    onChange={handleChange}
                  >
                    <MenuItem value="XS">XS</MenuItem>
                    <MenuItem value="S">S</MenuItem>
                    <MenuItem value="M">M</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="XL">XL</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Room Requirements"
                  value={student.roomRequirements || ''}
                  onChange={handleChange}
                  fullWidth
                  name="roomRequirements"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Class Requirements"
                  value={student.classRequirements || ''}
                  onChange={handleChange}
                  fullWidth
                  name="classRequirements"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Allergies"
                  value={student.allergies || ''}
                  onChange={handleChange}
                  fullWidth
                  name="allergies"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Notes"
                  value={student.notes || ''}
                  onChange={handleChange}
                  fullWidth
                  name="notes"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label="Emergency Contact"
                  value={student.emergencyContact || ''}
                  onChange={handleChange}
                  fullWidth
                  name="emergencyContact"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.mobilePhoneOptOut}
                      onChange={handleChange}
                      name="mobilePhoneOptOut"
                    />
                  }
                  label="Mobile Phone Opt-Out"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ width: '100%', borderTop: '1px solid #e0e0e0', my: 2 }} />
              </Grid>

              <Grid item xs={8}>
                <Typography sx={{ mb: 0, mt: 0 }}>Permissions</Typography>
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasAllPermission}
                      onChange={handleChange}
                      name="hasAllPermission"
                    />
                  }
                  label="All"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasPoolPermission}
                      onChange={handleChange}
                      name="hasPoolPermission"
                    />
                  }
                  label="Swimming Pool"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasPhotoPermission}
                      onChange={handleChange}
                      name="hasPhotoPermission"
                    />
                  }
                  label="Photography"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasMedicalPermission}
                      onChange={handleChange}
                      name="hasMedicalPermission"
                    />
                  }
                  label="Medical"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasHospitalPermission}
                      onChange={handleChange}
                      name="hasHospitalPermission"
                    />
                  }
                  label="Hospital"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasExcursionPermission}
                      onChange={handleChange}
                      name="hasExcursionPermission"
                    />
                  }
                  label="Excursions"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasActivityPermission}
                      onChange={handleChange}
                      name="hasActivityPermission"
                    />
                  }
                  label="Activity"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={student.hasSupervisionPermission}
                      onChange={handleChange}
                      name="hasSupervisionPermission"
                    />
                  }
                  label="Supervision"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ width: '100%', borderTop: '1px solid #e0e0e0', my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Copy from existing student (optional)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Template Student ID"
                  value={templateStudentId}
                  onChange={(e) => setTemplateStudentId(e.target.value)}
                  fullWidth
                  helperText="Copies stays, flight details and booked products"
                  sx={{ mb: 0, mt: 0 }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          elevation={6}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

export default AddStudent;
